<script setup lang="ts">
import { syncRef, useFocus, watchDebounced, watchOnce } from '@vueuse/core';
import { computed, onMounted, ref, useSlots } from 'vue';

const focused = defineModel<boolean>('focused', { default: false });

const slots = useSlots();

const getRoundedClass = () => {
  let classes = 'rounded-[calc(var(--tml-input-radius)-1px)]';

  if (slots.before) {
    classes += ' rounded-l-none';
  }

  if (slots.after) {
    classes += ' rounded-r-none';
  }

  return classes;
};

const props = withDefaults(
  defineProps<{
    modelValue?: string | number | null;
    autofocus?: boolean;
    name?: string;
    type?: string;
    rows?: number;
    autocomplete?: string;
    step?: number;
    min?: number | string;
    max?: number | string;
    tabindex?: string | number;
    readonly?: boolean;
    pattern?: string;
    title?: string;
    disabled?: boolean;
    required?: boolean;
    placeholder?: string;
    small?: boolean;
    maxlength?: number | string;
    list?: string;
    resize?: boolean;
    centerText?: boolean;
  }>(),
  {
    modelValue: undefined,
    autofocus: false,
    name: undefined,
    type: 'text',
    rows: 1,
    autocomplete: undefined,
    step: undefined,
    min: undefined,
    max: undefined,
    tabindex: 0,
    readonly: false,
    pattern: undefined,
    title: undefined,
    disabled: false,
    required: false,
    placeholder: undefined,
    small: false,
    maxlength: undefined,
    list: undefined,
    resize: true,
    centerText: false,
  },
);

const emit = defineEmits<{
  (event: 'update:model-value', value: string | number): void;
  (event: 'blur'): void;
  (event: 'focus'): void;
}>();

const padding = computed(() => {
  return props.small
    ? props.type === 'number'
      ? '0.2rem 0.2rem 0.2rem 0.3rem'
      : '0.2rem 0.6rem'
    : props.type === 'number'
      ? '0.5rem 0.3rem 0.5rem 0.5rem'
      : '0.5rem 1rem';
});

onMounted(() => {
  watchOnce(
    () => props.autofocus,
    (value) => {
      if (!value) {
        return;
      }

      focused.value = true;
    },
    { immediate: true },
  );
});

const input = ref<HTMLInputElement | null>(null);
const { focused: inputFocused } = useFocus(input, {
  initialValue: props.autofocus || focused.value,
});

syncRef(focused, inputFocused);

// for numeric inputs the min is enforced after a debounce - this gives the user time to finish typing
watchDebounced(
  () => props.modelValue,
  (value) => {
    if (props.type === 'number') {
      const numericValue = value as number;

      if (
        !isNaN(numericValue) &&
        numericValue !== null &&
        typeof numericValue === 'number' &&
        props.min
      ) {
        const clampedValue = Math.max(numericValue, Number(props.min));
        if (numericValue !== clampedValue) {
          emit('update:model-value', clampedValue);
        }
      }
    }
  },
  { debounce: 500 },
);

const getValueFromInput = (input: HTMLInputElement) => {
  if (input.type === 'number') {
    const value = input.valueAsNumber;
    if (!isNaN(value)) {
      const max = props.max !== undefined ? Number(props.max) : Infinity;
      const clampedValue = Math.min(value, max);
      if (value !== clampedValue) {
        input.value = clampedValue.toString();
      }
      return clampedValue;
    }
  }
  return input.value;
};

const component = computed(() => {
  if (props.type === 'textarea') {
    return 'textarea';
  }

  return props.rows > 1 ? 'textarea' : 'input';
});
</script>

<template>
  <div
    :class="[
      'flex items-center rounded-[--tml-input-radius] py-1',
      'text-[--tml-input-text-color]',
      'outline outline-[--tml-input-border-color] outline-[length:--tml-input-border-width] -outline-offset-1',
      'focus-within:outline-[--tml-input-focus-border-color] focus-within:outline-[length:--tml-input-focus-border-width]',
    ]"
    @mousedown="focused = true"
  >
    <slot name="before" />
    <component
      :is="component"
      :id="name"
      ref="input"
      :placeholder="placeholder"
      :value="modelValue"
      :name="name"
      :type="type"
      :rows="rows"
      :autocomplete="autocomplete"
      :step="step"
      :min="min"
      :max="max"
      :maxlength="maxlength"
      :pattern="pattern"
      :title="title"
      :style="{
        padding,
      }"
      class="grow -my-1 bg-transparent focus-within:outline-none min-w-0"
      :class="[getRoundedClass(), { 'resize-none': !resize, 'text-center': centerText }]"
      :tabindex="tabindex"
      :readonly="readonly"
      :disabled="disabled"
      :required="required"
      :list="list"
      @input="
        (event: Event) =>
          emit('update:model-value', getValueFromInput(event?.target as HTMLInputElement))
      "
      @blur="emit('blur')"
      @focus="emit('focus')"
    />
    <slot name="after" />
  </div>
</template>
